import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/** 与「使用准则」页共用的持久化键 */
export const TEMPLATE_PROTECTION_GUIDELINES_STORAGE_KEY = "template_protection_agreed_roles";

function readAgreedFromStorage(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(TEMPLATE_PROTECTION_GUIDELINES_STORAGE_KEY) === "1";
}

function persistAgreed(agreed: boolean) {
  if (agreed) {
    localStorage.setItem(TEMPLATE_PROTECTION_GUIDELINES_STORAGE_KEY, "1");
  } else {
    localStorage.removeItem(TEMPLATE_PROTECTION_GUIDELINES_STORAGE_KEY);
  }
}

export interface TemplateProtectionGuidelinesContextValue {
  /** 是否已阅读并同意黑名单服务使用准则（可持久化恢复） */
  hasAgreedToGuidelines: boolean;
  /** 由「使用准则」页勾选框调用；会同步写入 localStorage */
  setHasAgreedToGuidelines: (value: boolean) => void;
}

const TemplateProtectionGuidelinesContext =
  createContext<TemplateProtectionGuidelinesContextValue | null>(null);

export function TemplateProtectionGuidelinesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [hasAgreedToGuidelines, setAgreedState] = useState(readAgreedFromStorage);

  const setHasAgreedToGuidelines = useCallback((value: boolean) => {
    setAgreedState(value);
    persistAgreed(value);
  }, []);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === TEMPLATE_PROTECTION_GUIDELINES_STORAGE_KEY || e.key === null) {
        setAgreedState(readAgreedFromStorage());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({ hasAgreedToGuidelines, setHasAgreedToGuidelines }),
    [hasAgreedToGuidelines, setHasAgreedToGuidelines],
  );

  return (
    <TemplateProtectionGuidelinesContext.Provider value={value}>
      {children}
    </TemplateProtectionGuidelinesContext.Provider>
  );
}

export function useTemplateProtectionGuidelines(): TemplateProtectionGuidelinesContextValue {
  const ctx = useContext(TemplateProtectionGuidelinesContext);
  if (!ctx) {
    throw new Error(
      "useTemplateProtectionGuidelines 必须在 TemplateProtectionGuidelinesProvider 内使用",
    );
  }
  return ctx;
}
